import type { RealtimeTicket } from '../model.js';
import {
  describeRealtimeFailure,
  planReconnection,
  REALTIME_BURST_MS,
  type RealtimeStatus,
} from './realtime-connection.js';

/**
 * The live link to Virtual Profiler, with no socket in it.
 *
 * The lifecycle of a connection that mints a ticket, opens a transport, collapses bursts of
 * events and heals itself — expressed against ports, so that all of it can be tested without a
 * server, a browser or a socket. `use-queue-realtime.ts` supplies the real ports: socket.io on
 * one side, vue-query on the other.
 *
 * ## What it guarantees, and why each one is here
 *
 * - **Every attempt mints its own ticket.** The ticket is single use and 60 s long; the one the
 *   previous attempt presented is already spent. This is the part that gets forgotten, so it is
 *   the first thing the spec asserts.
 * - **An event is never data.** The only thing an event does is call `invalidate`. Nothing on
 *   this screen is painted from a socket payload, so a lost event costs a few seconds of
 *   latency and can never cost correctness — and there are not two paths by which the same read
 *   model arrives.
 * - **Nothing survives `stop`.** No transport, no timer, no pending invalidation. A screen that
 *   was navigated away from must stop existing, not stop being visible.
 * - **A stale attempt cannot speak.** Each attempt carries a generation; handlers of a
 *   superseded or stopped attempt return without touching anything. Without it, a socket that
 *   errors while its replacement is already connecting would report the new connection as dead.
 */

/** A live transport, from the link's point of view: something that can be closed. */
export interface RealtimeTransport {
  close(): void;
}

/** Everything that can happen to a connection. Exactly three things. */
export interface RealtimeTransportHandlers {
  onOpen(): void;
  /** Something changed upstream. No payload on purpose: see above. */
  onEvent(): void;
  onClosed(cause: unknown): void;
}

export interface RealtimeLinkPorts {
  /** Mints a fresh, single-use ticket. Called once per attempt, and its answer is never kept. */
  mintTicket(): Promise<RealtimeTicket>;
  open(ticket: RealtimeTicket, handlers: RealtimeTransportHandlers): RealtimeTransport;
  /** A burst of events has settled: the read model is stale and has to be asked for again. */
  invalidate(): void;
  publish(status: RealtimeStatus, error: string | null): void;
  /** Only the spec passes this: it is the burst window, in milliseconds. */
  burstMs?: number;
}

export interface RealtimeLink {
  start(): void;
  stop(): void;
}

export function createRealtimeLink(ports: RealtimeLinkPorts): RealtimeLink {
  const burstMs = ports.burstMs ?? REALTIME_BURST_MS;

  let transport: RealtimeTransport | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let burstTimer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;
  let lastError: string | null = null;
  let running = false;
  /** Bumped by every attempt and by `stop`, so a late handler can tell it is late. */
  let generation = 0;

  function closeTransport(): void {
    transport?.close();
    transport = null;
  }

  /**
   * One invalidation per burst, on a fixed window and not a sliding one.
   *
   * A window that restarted on every event would never close while events keep arriving — which
   * is precisely when the screen most needs to refresh. The first event of a burst sets the
   * deadline and the rest join it.
   */
  function onEvent(): void {
    if (burstTimer !== null) return;

    burstTimer = setTimeout(() => {
      burstTimer = null;
      ports.invalidate();
    }, burstMs);
  }

  function fail(cause: unknown): void {
    closeTransport();
    failures += 1;
    lastError = describeRealtimeFailure(cause);

    const plan = planReconnection(failures, cause);

    if (plan.action === 'give-up') {
      ports.publish('error', lastError);
      return;
    }

    ports.publish('retrying', lastError);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void attach();
    }, plan.delayMs);
  }

  async function attach(): Promise<void> {
    const mine = ++generation;
    const current = (): boolean => running && mine === generation;

    ports.publish(failures === 0 ? 'connecting' : 'retrying', lastError);

    let ticket: RealtimeTicket;

    try {
      ticket = await ports.mintTicket();
    } catch (cause) {
      // Checked after awaiting and not before: unmounting during the request is ordinary, and
      // reporting a failure for a link nobody is watching would resurrect its retry loop.
      if (current()) fail(cause);
      return;
    }

    if (!current()) return;

    try {
      transport = ports.open(ticket, {
        onOpen: () => {
          if (!current()) return;

          failures = 0;
          lastError = null;
          ports.publish('live', null);
        },
        onEvent: () => {
          if (current()) onEvent();
        },
        onClosed: (cause) => {
          if (current()) fail(cause);
        },
      });
    } catch (cause) {
      // `open` throwing is a bad URL or a socket.io that refuses to be built at all. Same
      // treatment: it is a failed attempt, not a crash of the screen around it.
      fail(cause);
    }
  }

  return {
    start(): void {
      if (running) return;

      running = true;
      failures = 0;
      lastError = null;
      void attach();
    },

    stop(): void {
      running = false;
      // Bumped so that anything still in flight — an awaited ticket, a socket that has not
      // noticed it is closed — finds itself out of date and stays quiet.
      generation += 1;

      if (retryTimer !== null) clearTimeout(retryTimer);
      if (burstTimer !== null) clearTimeout(burstTimer);
      retryTimer = null;
      burstTimer = null;

      closeTransport();
    },
  };
}
